import { useFetchData } from "../../../hooks/useFetch";
import { BaseResponse } from "../../../models/response/base-response";
import { ApiConfig, API_CUSTOMER_PRICING_ROUTER } from "../../../services/api-config";

// ============================================
// TYPES
// ============================================

export interface CustomerPricing {
  id: number;
  customer_id: number;
  service_type_map_id: number;
  branch_id: number;
  original_price: number;
  custom_price: number;
  difference_type?: number;
  difference_value?: number;
  status: number;
  note?: string;
  created_at: string;
  updated_at: string;
  // Service info from join
  service_name?: string;
  service_type_name?: string;
  /** Đơn vị tính của dịch vụ (units.name qua service_type_maps.unit_id) — "" nếu dịch vụ chưa gán đơn vị */
  unit_name?: string;
}

/** 1 dòng của danh sách khách hàng có giá riêng (API phân trang). */
export interface PricingCustomer {
  customer_id: number;
  pricing_count: number;
}

export interface PricingCustomerPage {
  list: PricingCustomer[];
  total_record: number;
  limit: number;
}

export interface ServicePreviewItem {
  service_type_map_id: number;
  service_id: number;
  service_type_id: number;
  service_name: string;
  service_type_name: string;
  /** Mã sản phẩm (service_types.code) */
  service_type_code?: string;
  original_price: number;
  custom_price: number;
  calculated_price: number;
  is_saved: boolean;
  existing_id: number | null;
}

export interface PreviewResponse {
  customer_id: number;
  branch_id: number;
  difference_type?: number;
  difference_value?: number;
  services: ServicePreviewItem[];
}

export interface PricingItem {
  service_type_map_id: number;
  original_price: number;
  custom_price: number;
  difference_type?: number;
  difference_value?: number;
}

// ============================================
// SERVICE
// ============================================

export const CustomerPricingService = {
  /**
   * Lấy danh sách giá riêng theo filter
   */
  list: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<BaseResponse<CustomerPricing[]>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (params: { customer_id?: number; branch_id?: number; status?: number } = {}) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_LIST,
          data: params,
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /**
   * Danh sách KHÁCH HÀNG có giá riêng, phân trang phía BE — mỗi dòng 1 khách kèm số dịch vụ có giá riêng.
   * Chi tiết giá của từng khách lấy qua `list({ customer_id })` khi mở dòng.
   */
  listCustomers: () => {
    const { request, baseResponse, loading } = useFetchData.Get<PricingCustomerPage>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      // `customer_ids`: id khách cách nhau bởi dấu phẩy — chỉ lấy các khách này (ô tìm kiếm khách hàng).
      fetch: (params: { page: number; limit: number; customer_ids?: string }) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_CUSTOMERS,
          data: params,
        }),
      response: baseResponse,
      loading,
    };
  },

  /**
   * Preview giá với chênh lệch
   * Product service tự động lấy danh sách service_type_maps theo branch_id
   */
  preview: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<PreviewResponse>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (payload: {
        customer_id: number;
        branch_id: number;
        difference_type?: number;
        difference_value?: number;
      }) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_PREVIEW,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  /**
   * Lưu nhiều giá cùng lúc
   */
  bulkSave: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<{ saved_count: number }>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (payload: {
        customer_id: number;
        branch_id: number;
        set_default_branch?: boolean;
        pricings: PricingItem[];
      }) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_BULK_SAVE,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  /**
   * Lưu 1 giá đơn lẻ
   */
  singleSave: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<{ id: number; is_new: boolean }>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (payload: {
        customer_id: number;
        branch_id: number;
        service_type_map_id: number;
        original_price: number;
        custom_price: number;
        difference_type?: number;
        difference_value?: number;
        note?: string;
      }) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_SINGLE_SAVE,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  /**
   * Lấy tất cả giá của khách hàng
   */
  byCustomer: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<BaseResponse<CustomerPricing[]>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (customerId: number) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_BY_CUSTOMER(customerId),
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /**
   * Lấy giá của khách theo chi nhánh
   */
  byCustomerBranch: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<BaseResponse<CustomerPricing[]>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (customerId: number, branchId: number) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_BY_CUSTOMER_BRANCH(customerId, branchId),
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  /**
   * Xóa 1 giá
   */
  delete: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<null>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_DELETE(id),
          data: {},
        }),
      response: baseResponse,
      loading,
    };
  },

  /**
   * Xóa tất cả giá của khách theo chi nhánh
   */
  deleteByCustomerBranch: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<{ deleted_count: number }>>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (customerId: number, branchId: number) =>
        request({
          endPoint: API_CUSTOMER_PRICING_ROUTER.API_DELETE_BY_CUSTOMER_BRANCH,
          data: { customer_id: customerId, branch_id: branchId },
        }),
      response: baseResponse,
      loading,
    };
  },
};

export default CustomerPricingService;
